import { toCatalogItemInputs, type CatalogCategoryInput } from './catalogItems'

/** Why a line of a repeated order cannot go back in the cart, or that it can. */
export type ReorderAvailability = 'available' | 'outOfStock' | 'notInCatalog'
export type UnavailableReason = Exclude<ReorderAvailability, 'available'>

export interface ReorderLine {
  sku: string
  quantity: number
  /** The catalog's name when it has one; the raw sku is all that is left when it does not. */
  label: string
  availability: ReorderAvailability
}

/** A line that carries enough to be counted and put back. */
export interface ReorderQuantityInput {
  sku: string
  quantity: number
}

export const isUnavailable = (
  line: ReorderLine,
): line is ReorderLine & { availability: UnavailableReason } =>
  line.availability !== 'available'

/**
 * `sku -> units`, which is the only shape a cart refill needs.
 *
 * Summed rather than assigned: the same sku can arrive on two lines — units
 * and a case, or the same product in two groups — and those are one entry
 * asking for the combined quantity, not two writes where the second wins.
 */
export const toReorderQuantities = (
  lines: readonly ReorderQuantityInput[],
): Record<string, number> =>
  lines.reduce<Record<string, number>>((acc, line) => {
    acc[line.sku] = (acc[line.sku] ?? 0) + line.quantity
    return acc
  }, {})

/**
 * Every line of an order the shopper wants again, resolved against what the
 * catalog still sells.
 *
 * An order is a record of what *was* bought, so time is the whole problem: a
 * sku can have been delisted or gone out of stock since, and writing it back
 * into a cart unchecked produces a cart two items short with nothing saying
 * why. This says which lines survive and, for the ones that do not, which of
 * the two reasons applies — the caller needs the difference because "sin
 * stock" is a wait and "ya no está en el catálogo" is not.
 *
 * `categories` must be a catalog view that could contain every sku asked
 * about. A single page of a paginated catalog cannot: a sku on page two is
 * absent from page one and would be reported `notInCatalog`, which is a
 * fabricated claim about a product that is still perfectly sellable.
 */
export const toReorderLines = (
  quantities: Readonly<Record<string, number>>,
  categories: readonly CatalogCategoryInput[] | null | undefined,
): ReorderLine[] => {
  const bySku = new Map(toCatalogItemInputs({ categories }).map((item) => [item.sku, item]))

  return Object.entries(quantities).map(([sku, quantity]) => {
    const item = bySku.get(sku)
    if (!item) return { sku, quantity, label: sku, availability: 'notInCatalog' }
    return {
      sku,
      quantity,
      label: item.name || sku,
      availability: item.stock <= 0 ? 'outOfStock' : 'available',
    }
  })
}
